'use client';

import { faCalendarAlt, faEdit, faLock, faMoneyBill } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import ImageSlider from '@pickleballinc/ui/components/ui/image-slider';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import FavoriteButton from '@/components/Favorite/FavoriteButton';
import { IsManagerDetails } from '@/legacy/utils/tournaments.types';
import { useSession } from '@/lib/SessionProvider';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { useAppConfig } from '@/providers/applicationConfig/ApplicationConfigProvider';
import ShareModal from '@/tournament/components/details/ShareModal';
import { ImageLightbox } from '@/tournament/components/images/ImageLightbox';
import { useGetUserUpcomingTournament } from '@/tournament/hooks/useGetUserUpcomingTournament';
import { GoverningBody, TournamentRedirectAction } from '@/types/types';
import { useImpersonationBarHeight } from '@/utils/hooks/useImpersonationBarHeight';

interface TourneyHeaderProps {
	data: TournamentHeaderInfo;
	slug?: string;
	isManagerDetails: IsManagerDetails;
	hasSponsors: boolean;
	governingBodies: GoverningBody[];
}

interface TournamentHeaderInfo {
	tourneyId: string;
	imgUrl: string;
	title: string;
	numOfPlayers: number;
	status: string;
	tourneyDates: string;
	ticketsUrl?: string;
	website?: string;
	regOpenDate: string;
	regCloseDate: string;
	currentRegPrice: string;
	venueName: string;
	street: string;
	cityStateZip: string;
	tourneyPlayersHidden: boolean;
	additionalPhotoUrls: string[];
	flyerFileUrl?: string;
	canPreRegister: boolean;
	udfButton: string;
	isAdvertiseOnly: boolean;
	dateStart: string;
	isFavorite?: boolean;
	onlineStore?: string;
	balanceDue: boolean;
	attendeeHeaderId?: string;
}

export const Header = ({ data, slug, isManagerDetails, hasSponsors, governingBodies }: TourneyHeaderProps) => {
	const {
		tourneyId,
		imgUrl,
		title,
		numOfPlayers,
		status,
		tourneyDates,
		ticketsUrl,
		website,
		tourneyPlayersHidden,
		additionalPhotoUrls,
		flyerFileUrl,
		canPreRegister,
		udfButton,
		isAdvertiseOnly,
		dateStart,
		isFavorite,
		onlineStore,
		balanceDue,
		attendeeHeaderId
	} = data;

	const pathname = usePathname();
	const { session } = useSession();

	const { hasPermission } = useAuth();

	const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

	const [isSticky, setIsSticky] = useState(false);

	const { data: upcomingTournament } = useGetUserUpcomingTournament();

	const getMyMatchesURL = () => {
		if (!upcomingTournament?.eventId) return;

		const baseURL = `https://brackets.${process.env.NEXT_PUBLIC_REWRITE_DOMAIN_NAME}/tournaments/${slug}/events/${upcomingTournament.eventId}/player-view`;

		return upcomingTournament.isCurrentMatch ? `${baseURL}/current-match` : `${baseURL}/matches`;
	};

	const {
		config: { isImpersonationInProcess }
	} = useAppConfig();

	const barHeight = useImpersonationBarHeight();

	const contactURL = `https://contact.${process.env.NEXT_PUBLIC_FRONT_URL}/contacts/tournament/${tourneyId}`;

	const [manageURL, setManageURL] = useState<string>('');

	useEffect(() => {
		let url: URL;

		if (session?.user && session?.user?.uuid) {
			url = new URL(`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx`);
			url.searchParams.set('mf', 't');
			url.searchParams.set('mid', tourneyId);
			url.searchParams.set('todo', TournamentRedirectAction.MANAGE);
			url.searchParams.set('rurl', window.location.href);
		} else {
			url = new URL(`${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/`);
			url.searchParams.set('flow', 'SelfService');
			url.searchParams.set('continue', window.location.href);
		}

		setManageURL(url.toString());
	}, []);

	const [registerURL, setRegisterURL] = useState<string>('');

	useEffect(() => {
		let url: URL;

		if (session?.user && session?.user?.uuid) {
			url = new URL(`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx`);
			url.searchParams.set('mf', 't');
			url.searchParams.set('mid', tourneyId);
			url.searchParams.set('todo', TournamentRedirectAction.REGISTER);
			url.searchParams.set('rurl', window.location.href);
		} else {
			url = new URL(`${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/`);
			url.searchParams.set('flow', 'SelfService');
			url.searchParams.set('continue', window.location.href);
		}

		setRegisterURL(url.toString());
	}, []);

	const [balanceDueURL, setBalanceDueURL] = useState<string>('');

	useEffect(() => {
		let url: URL;

		if (session?.user && session?.user?.uuid && attendeeHeaderId) {
			url = new URL(`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx`);
			url.searchParams.set('mf', 't');
			url.searchParams.set('mid', tourneyId);
			url.searchParams.set('todo', TournamentRedirectAction.BALANCE_DUE);
			url.searchParams.set('eahid', attendeeHeaderId);
			url.searchParams.set('rurl', window.location.href);
		} else {
			url = new URL(`${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/`);
			url.searchParams.set('flow', 'SelfService');
			url.searchParams.set('continue', window.location.href);
		}

		setBalanceDueURL(url.toString());
	}, []);

	const [udfURL, setUdfURL] = useState<string>('');

	useEffect(() => {
		const udfURL = new URL(`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx`);
		udfURL.searchParams.set('mf', 't');
		udfURL.searchParams.set('mid', tourneyId);
		udfURL.searchParams.set('todo', TournamentRedirectAction.UDF);
		udfURL.searchParams.set('rurl', window.location.href);

		setUdfURL(udfURL.toString());
	}, []);

	const mainHeaderRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setIsSticky(pathname !== `/tournaments/${slug}`);

		if (pathname === `/tournaments/${slug}`) {
			// Enable sticky header if Main Header is out of view
			const observer = new IntersectionObserver(
				([entry]) => {
					setIsSticky(!entry?.isIntersecting);
				},
				{
					threshold: 0,
					rootMargin: '0px 0px 0px 0px'
				}
			);

			if (mainHeaderRef.current) {
				observer.observe(mainHeaderRef.current);
			}

			return () => {
				if (mainHeaderRef.current) {
					observer.unobserve(mainHeaderRef.current);
				}
			};
		}

		return;
	}, [pathname]);

	const canShowPlayers = isSuperAdmin || isManagerDetails.IsManager || !tourneyPlayersHidden;

	const tourneyImages = [imgUrl, ...additionalPhotoUrls];
	const [activeImage, setActiveImage] = useState<string | null>();
	const [isFavorited, setIsFavorited] = useState(isFavorite || false);

	const tournamentInfo = useState({
		name: title,
		location: `${data.venueName}, ${data.cityStateZip}`,
		dateRange: tourneyDates,
		logoUrl: imgUrl
	})[0];
	const [isShareModalOpen, setIsShareModalOpen] = useState(false);
	const [shareURL, setShareURL] = useState<string>('');

	useEffect(() => {
		setShareURL(window.location.href);
	}, []);

	const headerActionButtons = (
		<>
			{session?.user?.token && balanceDue && (
				<a aria-label="Link to resolve your Balance Due" className="contents" href={balanceDueURL}>
					<Button prefixIcon={<FontAwesomeIcon icon={faMoneyBill} />} className="w-full justify-center bg-blue-600" variant="blue">
						Balance Due
					</Button>
				</a>
			)}

			{upcomingTournament && upcomingTournament.eventId !== '' && (
				<a aria-label="Link to My Matches" className="contents" href={getMyMatchesURL()}>
					<Button className="w-full justify-center border !border-blue-300 hover:!bg-blue-100 !text-blue-700 !bg-blue-50" variant="blue">
						My Matches
					</Button>
				</a>
			)}

			{session?.user?.token && (isSuperAdmin || !!isManagerDetails.IsManager) && !isSticky && (
				<a href={manageURL} className="hidden sm:contents" aria-label="Link to manage tournament">
					<Button
						prefixIcon={<FontAwesomeIcon icon={faEdit} />}
						className="w-full justify-center border !bg-gray-100/50"
						variant="secondary"
					>
						Manage Tournament
					</Button>
				</a>
			)}

			{isAdvertiseOnly ? (
				<>
					{website && (
						<a rel="noopener noreferrer" aria-label="Link to website (opens in a new tab)" className="contents" href={website}>
							<Button className="w-full justify-center bg-blue-600" variant="blue">
								Registration Info
							</Button>
						</a>
					)}
				</>
			) : isManagerDetails.IsRegistered && !['no-reg', 'reg-closed', 'completed'].includes(status) ? (
				<a aria-label="Link to Edit your Registration" className="contents" href={registerURL}>
					<Button prefixIcon={<FontAwesomeIcon icon={faEdit} />} className="w-full justify-center bg-blue-600" variant="blue">
						Edit Registration
					</Button>
				</a>
			) : (
				<>
					{status === 'reg-open' || canPreRegister ? (
						<>
							<a aria-label="Link to Register for the Tournament" className="contents" href={registerURL}>
								<Button className="w-full justify-center bg-blue-600" variant="blue">
									Register Now
								</Button>
							</a>
							{udfButton && (
								<a className="contents" href={udfURL.toString()}>
									<Button className="w-full justify-center border !bg-gray-100/50 hover:!bg-gray-100" variant="secondary">
										{udfButton}
									</Button>
								</a>
							)}
						</>
					) : status === 'completed' ? (
						<Button
							prefixIcon={<FontAwesomeIcon icon={faLock} />}
							className="pointer-events-none w-full justify-center border !bg-gray-100/50"
							variant="secondary"
						>
							Completed
						</Button>
					) : status === 'reg-closed' ? (
						<a href={`${process.env.NEXT_PUBLIC_PT_URL}/tournaments/${slug}/events`} className="contents">
							<Button className="w-full justify-center bg-blue-600" variant="blue">
								Draws & Results
							</Button>
						</a>
					) : status === 'no-reg' ? (
						<Button
							prefixIcon={<FontAwesomeIcon icon={faLock} />}
							className="pointer-events-none w-full justify-center border !bg-gray-100/50"
							variant="secondary"
						>
							No Online Registration
						</Button>
					) : status === 'reg-full' ? (
						<Button
							prefixIcon={<FontAwesomeIcon icon={faLock} />}
							className="pointer-events-none w-full justify-center border !bg-gray-100/50"
							variant="secondary"
						>
							Registration Full
						</Button>
					) : (
						<Button
							prefixIcon={<FontAwesomeIcon icon={faLock} />}
							className="pointer-events-none w-full justify-center border !bg-gray-100/50"
							variant="secondary"
						>
							Registration opens on {data?.regOpenDate}
						</Button>
					)}
				</>
			)}
		</>
	);

	const tournamentCustomButtons = (
		<>
			<a className="contents" href={contactURL} aria-label="Link to contact tournament">
				<Button
					variant="link-gray"
					className="font-normal !text-gray-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100"
				>
					Contact Tournament
				</Button>
			</a>

			{ticketsUrl && (
				<>
					<div className="hidden w-px bg-gray-400 lg:block" />
					<a href={ticketsUrl} target="_blank" rel="noopener noreferrer" aria-label="Link to purchase tickets (opens in a new tab)">
						<Button
							variant="link-gray"
							className="font-normal !text-gray-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100"
						>
							Purchase Tickets
						</Button>
					</a>
				</>
			)}
			{website && (
				<>
					<div className="hidden w-px bg-gray-400 lg:block" />
					<a href={website} target="_blank" rel="noopener noreferrer" aria-label="Link to website (opens in a new tab)">
						<Button
							variant="link-gray"
							className="font-normal !text-gray-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100"
						>
							Website
						</Button>
					</a>
				</>
			)}

			{onlineStore && (
				<>
					<div className="hidden w-px bg-gray-400 lg:block" />
					<a href={onlineStore} target="_blank" rel="noopener noreferrer" aria-label="Link to online store (opens in a new tab)">
						<Button
							variant="link-gray"
							className="font-normal !text-gray-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100"
						>
							Online Store
						</Button>
					</a>
				</>
			)}

			<div className="hidden w-px bg-gray-400 lg:block" />
			<Button
				variant="link-gray"
				className="font-normal !text-gray-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100"
				onClick={() => setIsShareModalOpen(true)}
			>
				Share
			</Button>

			{flyerFileUrl && (
				<>
					<div className="hidden w-px bg-gray-400 lg:block" />
					<a href={flyerFileUrl} target="_blank" rel="noopener noreferrer" aria-label="Link to download flyer (opens in a new tab)">
						<Button variant="link-gray" className="font-normal !text-gray-900 underline">
							Download Flyer
						</Button>
					</a>
				</>
			)}
		</>
	);

	return (
		<>
			{/* Main Header */}
			{pathname === `/tournaments/${slug}` && (
				<div ref={mainHeaderRef} className="-mx-4 sm:mx-0">
					<div className="flex flex-col gap-3 bg-white p-3 sm:p-4 lg:px-6 md:gap-6">
						<h1 className="hidden text-xl font-semibold text-gray-900 md:flex lg:hidden">{title}</h1>

						<div
							className={`flex flex-col flex-wrap items-center gap-4 sm:gap-8 transition-all duration-300 md:flex-row md:flex-nowrap md:gap-6 lg:gap-0`}
						>
							<div className="flex flex-row w-full items-center justify-between md:hidden gap-2">
								<h1 className="flex bg-white text-base font-semibold text-gray-900 md:hidden sm:text-lg">{title}</h1>
								{process.env.NEXT_PUBLIC_APP_ENV !== 'prod' && session?.user?.uuid && (
									<FavoriteButton moduleUuid={tourneyId} isFavorited={isFavorited} setIsFavorited={setIsFavorited} isDetailsPage />
								)}
							</div>

							<div className="flex flex-col sm:flex-row w-full gap-4 md:w-[70%]">
								{additionalPhotoUrls && additionalPhotoUrls.length > 0 ? (
									<>
										<ImageSlider
											onImageClick={(image) => setActiveImage(image)}
											containerClassNames="sm:min-w-56 sm:min-h-56 h-36 sm:max-w-[45%]"
											images={tourneyImages}
										/>
										<ImageLightbox
											hasPreviousImage={tourneyImages.findIndex((image) => image === activeImage) > 0}
											hasNextImage={tourneyImages.findIndex((image) => image === activeImage) < tourneyImages.length - 1}
											totalCount={tourneyImages.length}
											currentImgNumber={tourneyImages.findIndex((image) => image === activeImage) + 1}
											onClose={() => setActiveImage(null)}
											image={activeImage}
											onNextClick={() =>
												setActiveImage(tourneyImages[tourneyImages.findIndex((image) => image === activeImage) + 1])
											}
											onPreviousClick={() =>
												setActiveImage(tourneyImages[tourneyImages.findIndex((image) => image === activeImage) - 1])
											}
										/>
									</>
								) : (
									<img
										className="sm:w-[300px] sm:max-h-none max-h-40 sm:max-w-[45%] object-contain transition-all duration-300"
										src={imgUrl}
										alt={`${title} logo`}
										aria-hidden="true"
									/>
								)}

								<div className="flex sm:hidden w-full gap-4 flex-col items-center">{headerActionButtons}</div>

								<div className="flex items-center sm:items-start flex-col justify-center gap-4 transition-all duration-300">
									<div className="hidden sm:flex flex-row gap-6">
										<h1 className="hidden flex-row items-center text-2xl max-w-[650px] font-semibold text-gray-900 lg:flex">
											{title}
										</h1>
									</div>
									<div className="sm:flex hidden flex-col gap-4 lg:flex-row">
										{(status || isAdvertiseOnly) && (
											<div className="w-fit my-auto rounded-md border px-2 py-1 text-sm font-medium text-gray-700">
												{!isAdvertiseOnly && <span style={{ color: 'green' }}>&bull; </span>}
												{canPreRegister ? (
													'Register Now'
												) : (
													<>
														{isAdvertiseOnly
															? 'Advertised'
															: status === 'completed'
																? 'Completed'
																: status === 'reg-closed'
																	? 'Registration Closed '
																	: status === 'no-reg'
																		? 'No Online Registration'
																		: status === 'reg-full'
																			? 'Registration Full'
																			: 'Register Now'}
													</>
												)}{' '}
												{canShowPlayers && !isAdvertiseOnly && (
													<span className="hidden sm:inline-flex"> - {numOfPlayers} players</span>
												)}
											</div>
										)}

										{process.env.NEXT_PUBLIC_APP_ENV !== 'prod' && session?.user?.uuid && (
											<div className="sm:flex hidden">
												<FavoriteButton
													moduleUuid={tourneyId}
													isFavorited={isFavorited}
													setIsFavorited={setIsFavorited}
													isDetailsPage={true}
												/>
											</div>
										)}
									</div>

									{status && canShowPlayers && !isAdvertiseOnly && (
										<div className="text-sm font-semibold text-gray-600 sm:hidden">{numOfPlayers} players registered</div>
									)}

									<div className="flex flex-row flex-wrap gap-4 sm:flex-nowrap">
										<div>
											<span className="mr-2">
												<FontAwesomeIcon className={!isSticky ? 'text-xl' : ''} icon={faCalendarAlt} />
											</span>
											{tourneyDates}
										</div>
										{/* <div className="hidden w-px bg-gray-400 sm:block" /> */}
										{/* <Button variant="link-gray" className={`${isSticky ? 'text-sm' : ''} font-normal !text-gray-900 underline`}>
								Add To Calendar
							</Button> */}
									</div>
									{!isSticky && (
										<div className="hidden flex-col flex-wrap  gap-4 sm:flex lg:flex-nowrap">
											{/* <div className="mr-4 flex gap-4 text-xl">
									<FontAwesomeIcon icon={faFacebook} />
									<FontAwesomeIcon icon={faTwitter} />
									<FontAwesomeIcon icon={faInstagram} />
								</div> */}
											<div className="hidden xl:w-max flex-wrap flex-row gap-2 lg:flex">{tournamentCustomButtons}</div>
										</div>
									)}
								</div>
							</div>
							{!isSticky && <div className="flex flex-wrap justify-center flex-row gap-4  md:hidden">{tournamentCustomButtons}</div>}

							<div className="sm:flex w-full hidden flex-col items-center gap-4 md:ml-auto md:w-auto">
								{headerActionButtons}
								{/* {!isSticky && (
						<Button
							className="rounded-none border-b border-blue-600 pb-[2px] text-sm font-semibold leading-3 text-blue-600 hover:!text-blue-700"
							variant="link"
						>
							12 partners needed
						</Button>
					)} */}
							</div>
						</div>
						<div className="hidden md:flex flex-wrap justify-center flex-row gap-4 lg:hidden">{tournamentCustomButtons}</div>
						{governingBodies && governingBodies.length > 0 && (
							<div className="overflow-x-scroll hidden-scrollbar">
								<div className="mx-auto flex w-max flex-row items-center justify-center gap-2 bg-white">
									{governingBodies.map((gb) => (
										<>
											{gb.imageUrl && (
												<div key={gb.imageUrl} className="flex flex-col items-center">
													<img
														width={110}
														src={gb.imageUrl}
														alt={`${gb.level} governing level image`}
														className="max-h-[70px]"
													/>
													<div className="mt-2 text-sm font-semibold text-gray-700">{gb.level}</div>
												</div>
											)}
										</>
									))}
								</div>
							</div>
						)}
					</div>

					{!isAdvertiseOnly && (
						<TourneyNav
							canShowPlayers={canShowPlayers}
							slug={slug || ''}
							pathname={pathname}
							tourneyStatus={status}
							hasSponsors={hasSponsors}
							hasAdditionalPhotos={additionalPhotoUrls && additionalPhotoUrls.length > 0}
							dateStart={dateStart}
						/>
					)}
				</div>
			)}

			{/* Sticky Header */}
			<div
				className={`${isSticky ? 'top-0' : '-top-[200vh] lg:-top-[200vh]'} ${
					pathname !== `/tournaments/${slug}` ? 'sticky -mx-4 sm:mx-auto' : 'fixed inset-x-0 '
				} z-10 flex-col bg-white transition-all duration-300`}
				style={isSticky && isImpersonationInProcess ? { top: `${barHeight}px` } : undefined}
				aria-hidden={isSticky ? 'false' : 'true'}
			>
				<div className="mx-auto flex max-w-[1400px] flex-col flex-wrap items-center gap-8 bg-white p-3 transition-all duration-300 md:flex-row md:flex-nowrap sm:gap-0 sm:p-4 sm:px-6">
					<div className="flex flex-row items-center gap-4">
						<h1 className="flex bg-white text-base font-semibold text-gray-900 sm:hidden sm:text-lg">{title}</h1>
						{process.env.NEXT_PUBLIC_APP_ENV !== 'prod' && session?.user?.uuid && (
							<div className="flex sm:hidden">
								<FavoriteButton
									moduleUuid={tourneyId}
									isFavorited={isFavorited}
									setIsFavorited={setIsFavorited}
									isDetailsPage={true}
								/>
							</div>
						)}
					</div>
					<div className="hidden w-full gap-4 sm:flex md:w-[70%]">
						<img className="w-[140px] max-w-[45%] object-contain transition-all duration-300" src={imgUrl} alt={`${title} logo`} />
						<div className="flex flex-col justify-center gap-2 transition-all duration-300">
							<h1 className="hidden flex-row items-center text-lg font-semibold text-gray-900 sm:flex">{title}</h1>
							<div className="flex flex-col gap-2 lg:flex-row">
								{(status || isAdvertiseOnly) && (
									<div className="w-fit my-auto rounded-md border px-2 py-1 text-sm font-medium text-gray-700">
										{!isAdvertiseOnly && <span style={{ color: 'green' }}>&bull; </span>}
										{canPreRegister ? (
											'Register Now'
										) : (
											<>
												{isAdvertiseOnly
													? 'Advertised'
													: status === 'completed'
														? 'Completed'
														: status === 'reg-closed'
															? 'Registration Closed '
															: status === 'no-reg'
																? 'No Online Registration'
																: status === 'reg-full'
																	? 'Registration Full'
																	: 'Register Now'}
											</>
										)}{' '}
										{canShowPlayers && !isAdvertiseOnly && <span className="inline-flex"> - {numOfPlayers} players</span>}
									</div>
								)}

								{process.env.NEXT_PUBLIC_APP_ENV !== 'prod' && session?.user?.uuid && (
									<div className="flex">
										<FavoriteButton
											moduleUuid={tourneyId}
											isFavorited={isFavorited}
											setIsFavorited={setIsFavorited}
											isDetailsPage={true}
										/>
									</div>
								)}
							</div>

							<div className="flex flex-row flex-wrap gap-2 text-sm sm:flex-nowrap">
								<div>
									<span className="mr-2">
										<FontAwesomeIcon icon={faCalendarAlt} />
									</span>
									{tourneyDates}
								</div>
								{/* <div className="hidden w-px bg-gray-400 sm:block" /> */}
								{/* <Button variant="link-gray" className={`${isSticky ? 'text-sm' : ''} font-normal !text-gray-900 underline`}>
								Add To Calendar
							</Button> */}
							</div>
						</div>
					</div>

					<div className="flex w-full flex-col items-center mt-2 md:mt-0 gap-4 md:ml-auto md:w-auto">
						{isAdvertiseOnly ? (
							<>
								{website && (
									<a rel="noopener noreferrer" aria-label="Link to Tournament's Website" className="contents" href={website}>
										<Button className="w-full justify-center bg-blue-600" variant="blue">
											Registration Info
										</Button>
									</a>
								)}
							</>
						) : isManagerDetails.IsRegistered && !['no-reg', 'reg-closed', 'completed'].includes(status) ? (
							<a aria-label="Link to edit your Registration" className="contents" href={registerURL}>
								<Button prefixIcon={<FontAwesomeIcon icon={faEdit} />} className="w-full justify-center bg-blue-600" variant="blue">
									Edit Registration
								</Button>
							</a>
						) : (
							<>
								{status === 'reg-open' || canPreRegister ? (
									<a aria-label="Link to Register for the Tournament" className="contents" href={registerURL}>
										<Button className="w-full justify-center bg-blue-600" variant="blue">
											Register Now
										</Button>
									</a>
								) : status === 'completed' ? (
									<Button
										prefixIcon={<FontAwesomeIcon icon={faLock} />}
										className="pointer-events-none w-full justify-center border !bg-gray-100/50"
										variant="secondary"
									>
										Completed
									</Button>
								) : status === 'reg-closed' ? (
									<a
										aria-label="Link to Draws & Results"
										href={`${process.env.NEXT_PUBLIC_PT_URL}/tournaments/${slug}/events`}
										className="contents"
									>
										<Button className="w-full justify-center bg-blue-600" variant="blue">
											Draws & Results
										</Button>
									</a>
								) : status === 'no-reg' ? (
									<Button
										prefixIcon={<FontAwesomeIcon icon={faLock} />}
										className="pointer-events-none w-full justify-center border !bg-gray-100/50"
										variant="secondary"
									>
										No Online Registration
									</Button>
								) : status === 'reg-full' ? (
									<Button
										prefixIcon={<FontAwesomeIcon icon={faLock} />}
										className="pointer-events-none w-full justify-center border !bg-gray-100/50"
										variant="secondary"
									>
										Registration Full
									</Button>
								) : (
									<Button
										prefixIcon={<FontAwesomeIcon icon={faLock} />}
										className="pointer-events-none w-full justify-center border !bg-gray-100/50"
										variant="secondary"
									>
										Registration opens on {data?.regOpenDate}
									</Button>
								)}
							</>
						)}

						{/* {!isSticky && (
						<Button
							className="rounded-none border-b border-blue-600 pb-[2px] text-sm font-semibold leading-3 text-blue-600 hover:!text-blue-700"
							variant="link"
						>
							12 partners needed
						</Button>
					)} */}
					</div>
				</div>
				{!isAdvertiseOnly && (
					<TourneyNav
						slug={slug || ''}
						pathname={pathname}
						tourneyStatus={status}
						hasSponsors={hasSponsors}
						canShowPlayers={canShowPlayers}
						hasAdditionalPhotos={additionalPhotoUrls && additionalPhotoUrls.length > 0}
						dateStart={dateStart}
					/>
				)}
			</div>
			<ShareModal
				open={isShareModalOpen}
				onOpenChange={() => setIsShareModalOpen(false)}
				title={title}
				tournamentInfo={tournamentInfo}
				url={shareURL}
				slug={slug}
			/>
		</>
	);
};

interface TourneyNavProps {
	slug: string;
	pathname: string;
	tourneyStatus: string;
	hasSponsors: boolean;
	canShowPlayers: boolean;
	hasAdditionalPhotos: boolean;
	dateStart: string;
}

const TourneyNav = ({ slug, pathname, tourneyStatus, hasSponsors, canShowPlayers, hasAdditionalPhotos, dateStart }: TourneyNavProps) => {
	const searchParams = useSearchParams();
	const { session } = useSession();

	const [playersURL, setPlayersURL] = useState<string>('');

	useEffect(() => {
		if (session?.user && session?.user?.uuid) {
			setPlayersURL(`/tournaments/${slug}/find-player`);
		} else {
			const url = new URL(`${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/`);
			url.searchParams.set('flow', 'SelfService');
			url.searchParams.set('continue', `${process.env.NEXT_PUBLIC_PT_URL}/tournaments/${slug}/find-player`);
			setPlayersURL(url.toString());
		}
	}, []);

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const tournamentDate = new Date(dateStart);
	tournamentDate.setHours(0, 0, 0, 0);

	return (
		<div
			className="overflow-y-hidden overflow-x-scroll bg-gray-100 p-3 sm:p-4 lg:overflow-hidden"
			style={{
				boxShadow: '0px 4px 8px -2px #1018281A'
			}}
		>
			<div className="flex w-max flex-row justify-center gap-4 lg:w-auto">
				{tourneyStatus === 'completed' && (
					<CustomLink
						href={`/tournaments/${slug}/winners-circle`}
						aria-current={pathname === `/tournaments/${slug}/winners-circle` ? 'page' : undefined}
						aria-label="Tournament Winners Circle"
					>
						<Button
							className={
								pathname === `/tournaments/${slug}/winners-circle`
									? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
									: 'bg-white'
							}
							variant="secondary"
						>
							Winners Circle
						</Button>
					</CustomLink>
				)}
				<CustomLink
					href={`/tournaments/${slug}`}
					aria-current={pathname === `/tournaments/${slug}` ? 'page' : undefined}
					aria-label="Tournament Details"
				>
					<Button
						className={pathname === `/tournaments/${slug}` ? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200' : 'bg-white'}
						variant="secondary"
					>
						Details
					</Button>
				</CustomLink>
				<CustomLink
					href={`/tournaments/${slug}/events`}
					aria-current={pathname === `/tournaments/${slug}/events` && !searchParams.get('needAPartner') ? 'page' : undefined}
					aria-label="Tournament Events"
				>
					<Button
						className={
							pathname === `/tournaments/${slug}/events` && !searchParams.get('needAPartner')
								? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
								: 'bg-white'
						}
						variant="secondary"
					>
						Events
					</Button>
				</CustomLink>
				{today >= tournamentDate && (
					<CustomLink
						href={`/tournaments/${slug}/results`}
						aria-current={pathname === `/tournaments/${slug}/results` ? 'page' : undefined}
						aria-label="Tournament Results"
					>
						<Button
							className={
								pathname === `/tournaments/${slug}/results`
									? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
									: 'bg-white'
							}
							variant="secondary"
						>
							Results
						</Button>
					</CustomLink>
				)}

				{canShowPlayers && (
					<CustomLink
						href={playersURL}
						aria-current={pathname === `/tournaments/${slug}/find-player` ? 'page' : undefined}
						aria-label="Tournament Players"
					>
						<Button
							className={
								pathname === `/tournaments/${slug}/find-player`
									? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
									: 'bg-white'
							}
							variant="secondary"
						>
							Players
						</Button>
					</CustomLink>
				)}

				{tourneyStatus !== 'completed' && canShowPlayers && (
					<CustomLink
						href={`/tournaments/${slug}/events?needAPartner=true`}
						aria-current={pathname === `/tournaments/${slug}/events` && searchParams.get('needAPartner') ? 'page' : undefined}
						aria-label="Tournament Find a Partner"
					>
						<Button
							className={
								pathname === `/tournaments/${slug}/events` && searchParams.get('needAPartner')
									? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
									: 'bg-white'
							}
							variant="secondary"
						>
							Find a Partner
						</Button>
					</CustomLink>
				)}

				{hasSponsors && (
					<CustomLink
						href={`/tournaments/${slug}/sponsors`}
						aria-current={pathname === `/tournaments/${slug}/sponsors` ? 'page' : undefined}
						aria-label="Tournament Sponsors"
					>
						<Button
							className={
								pathname === `/tournaments/${slug}/sponsors`
									? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
									: 'bg-white'
							}
							variant="secondary"
						>
							Sponsors
						</Button>
					</CustomLink>
				)}

				{tourneyStatus !== 'completed' && (
					<CustomLink
						href={`/tournaments/${slug}/get-involved`}
						aria-current={pathname === `/tournaments/${slug}/get-involved` ? 'page' : undefined}
						aria-label="Tournament Get Involved"
					>
						<Button
							className={
								pathname === `/tournaments/${slug}/get-involved`
									? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
									: 'bg-white'
							}
							variant="secondary"
						>
							Get Involved
						</Button>
					</CustomLink>
				)}

				<CustomLink href={`/tournaments/${slug}/stats`}>
					<Button
						className={
							pathname === `/tournaments/${slug}/stats` ? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200' : 'bg-white'
						}
						variant="secondary"
					>
						Stats
					</Button>
				</CustomLink>

				{hasAdditionalPhotos && (
					<CustomLink
						href={`/tournaments/${slug}/photos`}
						aria-current={pathname === `/tournaments/${slug}/photos` ? 'page' : undefined}
						aria-label="Tournament Photos"
					>
						<Button
							className={
								pathname === `/tournaments/${slug}/photos`
									? 'border-blue-200 bg-blue-100 !text-blue-900 hover:!bg-blue-200'
									: 'bg-white'
							}
							variant="secondary"
						>
							Photos
						</Button>
					</CustomLink>
				)}
			</div>
		</div>
	);
};
